import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n/index.ts';
import { Chip } from '../../ui/index.ts';

/**
 * Read-only "how many platform SKUs does this upstream model serve"
 * badge (epic #1327 PR-5).
 *
 * Deliberately offers no write: which SKUs a model serves is decided on
 * the SKU detail page, and a shortcut here would be the second binding
 * writer this PR exists to remove. The link is a read-only jump into the
 * market so an operator can get to that page without hunting for it.
 *
 * `count === null` means "we could not count", which is a different fact
 * from zero — a truncated routing summary would otherwise render as "0
 * SKUs" for a model that in fact serves many.
 */
export function ServingSkuBadge({
  capabilityId,
  count,
}: {
  readonly capabilityId: string;
  readonly count: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col items-start gap-1">
      <Chip
        tone={count === null ? 'neutral' : count > 0 ? 'ok' : 'neutral'}
        testId={`upstream-model-serving-${capabilityId}`}
      >
        {count === null
          ? t('upstream_detail.routing.serving.unknown')
          : t('upstream_detail.routing.serving.count', { count })}
      </Chip>
      <Link
        to="/supply/market"
        className="inline-flex items-center gap-1 text-micro text-signal transition-colors duration-120 hover:text-signal-bright"
        data-testid={`upstream-model-serving-link-${capabilityId}`}
      >
        {t('upstream_detail.routing.serving.open_market')}
        <ExternalLink aria-hidden="true" className="h-3 w-3" />
      </Link>
    </div>
  );
}
